/**
 * Centralized privacy copy.
 *
 * These strings appear in multiple places (the first-visit modal, the
 * PlayerSetup form, and the full /privacy page) and MUST stay in sync. Edit
 * here only - never inline these phrases in components.
 *
 * Scope:
 * - Only strings that are reused (or paraphrase each other and are likely to
 *   drift) live here. Long-form, single-use policy paragraphs stay in
 *   `src/pages/Privacy.tsx` to keep this file focused.
 */

export const PRIVACY_COPY = {
  /** One-line tagline used in the modal header & policy callout. */
  shortTagline: "ElementPad doesn't collect or save your information.",

  /** Slightly longer headline framing for the policy callout. */
  shortTaglineHeadline: "Short version: we don't collect or save your information.",

  /** Used by the modal - first body paragraph. */
  noServerSummary:
    "No accounts, no user database, no server-side tracking. Anything you type, configure, play, or study - including game nicknames, scores, lab settings, worksheets, and Learn-tab flashcard progress - stays in your own browser.",

  /** Used by the modal - second body paragraph. */
  localPrefsSummary:
    "A few things (theme, accessibility, sound, solo high scores, and flashcard review progress) are kept in your browser's local storage so the app remembers your setup. Clear them any time from your browser settings.",

  /** Used by the policy page callout - long form of the no-server claim. */
  noServerLong:
    "ElementPad has no accounts, no user database, and no server-side tracking of who you are or what you do. Everything you type, configure, play, or study including game nicknames, solo high scores, lab settings, worksheet selections, accessibility preferences, and Learn-tab flashcard progress stays in your own browser. Nothing is transmitted to us, sold, or shared.",

  /** Reassurance shown under the player-name inputs in every hot-seat game. */
  sessionNamesNote: "Names exist only for this session and aren't stored.",

  /** Modal CTA labels. */
  ctaAcknowledge: "Got it",
  ctaReadFullPolicy: "Read full policy",
  modalTitle: "Your privacy, in one line",

  /** Path to the full policy. Centralized so future renames stay in sync. */
  policyPath: "/privacy",
} as const;

export type PrivacyCopy = typeof PRIVACY_COPY;